import { matchingAppointmentTypes } from '@/lib/appointmentTypeSearch';

/** How many forløb stay at the top of Vælg forløb after the user picks them. */
export const RECENT_COURSE_LIMIT = 5;

const STORAGE_PREFIX = 'clinio-v2-recent-courses';

export function recentCourseStorageKey(userId: string, clinicId: string): string {
  return `${STORAGE_PREFIX}:${userId}:${clinicId}`;
}

export function readRecentCourseIds(raw: string | null): string[] {
  if (!raw) return [];
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    const ids: string[] = [];
    const seen = new Set<string>();
    for (const value of parsed) {
      if (typeof value !== 'string') continue;
      const id = value.trim();
      if (!id || seen.has(id)) continue;
      seen.add(id);
      ids.push(id);
      if (ids.length >= RECENT_COURSE_LIMIT) break;
    }
    return ids;
  } catch {
    return [];
  }
}

/** Newest pick first. A repeat pick moves back to the front and does not duplicate. */
export function rememberRecentCourseId(ids: readonly string[], templateId: string): string[] {
  const id = templateId.trim();
  if (!id) return [...ids];
  return [id, ...ids.filter((value) => value !== id)].slice(0, RECENT_COURSE_LIMIT);
}

export function loadRecentCourseIds(userId: string, clinicId: string): string[] {
  if (typeof window === 'undefined' || !userId || !clinicId) return [];
  try {
    return readRecentCourseIds(
      window.localStorage.getItem(recentCourseStorageKey(userId, clinicId)),
    );
  } catch {
    return [];
  }
}

export function saveRecentCourseIds(
  userId: string,
  clinicId: string,
  ids: readonly string[],
): void {
  if (typeof window === 'undefined' || !userId || !clinicId) return;
  try {
    window.localStorage.setItem(recentCourseStorageKey(userId, clinicId), JSON.stringify([...ids]));
  } catch {
    /* ignore quota / private mode */
  }
}

export type CourseSearchRow = {
  id: string;
  name: string;
};

/**
 * Filter like Aftaletype.
 *
 * `favorites` are recent picks that still match, newest first, once each.
 * `available` is every other matching course, in the order given. Callers
 * pass the clinic's bookable forløb (aktive skabeloner med flere trin), so
 * that second list is tilgængelige forløb. A course is never in both.
 * Neither list selects a course.
 */
export function courseTypeaheadRows<T extends CourseSearchRow>(
  courses: readonly T[],
  query: string,
  recentIds: readonly string[],
): { favorites: T[]; available: T[] } {
  const matches = matchingAppointmentTypes(courses, query);
  const byId = new Map<string, T>();
  for (const course of matches) {
    if (!course.id || byId.has(course.id)) continue;
    byId.set(course.id, course);
  }

  const favorites: T[] = [];
  const favoriteIds = new Set<string>();
  for (const id of recentIds) {
    const course = byId.get(id);
    if (!course || favoriteIds.has(id)) continue;
    favoriteIds.add(id);
    favorites.push(course);
  }

  return {
    favorites,
    available: matches.filter((course) => course.id && !favoriteIds.has(course.id)),
  };
}
